// TC2: Sacred gold — Om pulsates, title radiates outward
import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill, spring } from "remotion";
import { loadFont as loadMontserrat } from "@remotion/google-fonts/MontserratAlternates";
import { loadFont as loadNoto } from "@remotion/google-fonts/NotoSansDevanagari";

const { fontFamily: mont } = loadMontserrat("normal", { weights: ["900"], subsets: ["latin"] });
const { fontFamily: noto } = loadNoto("normal", { weights: ["300", "700"], subsets: ["devanagari"] });

export const KedarnathTC2: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Om scale in
  const omSpring = spring({ frame, fps, config: { damping: 8, stiffness: 60 }, durationInFrames: Math.round(1.5 * fps) });
  const omScale = interpolate(omSpring, [0, 1], [0.3, 1]);
  const omRotate = interpolate(frame, [0, Math.round(2 * fps)], [90, 0], { extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });

  // Om pulse glow
  const omGlow = Math.sin(frame * 0.3) * 0.5 + 0.5;

  // Ripple rings expand from Om
  const ring1 = interpolate(frame, [Math.round(0.3 * fps), Math.round(1.5 * fps)], [0, 1], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });
  const ring2 = interpolate(frame, [Math.round(0.6 * fps), Math.round(1.8 * fps)], [0, 1], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });
  const ring3 = interpolate(frame, [Math.round(0.9 * fps), Math.round(2.1 * fps)], [0, 1], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });

  // Title letter-by-letter reveal
  const titleOpacity = interpolate(frame, [Math.round(1.2 * fps), Math.round(2 * fps)], [0, 1], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });
  const titleY = interpolate(frame, [Math.round(1.2 * fps), Math.round(2 * fps)], [30, 0], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });

  // Subtitle and divider
  const subOpacity = interpolate(frame, [Math.round(2.2 * fps), Math.round(3 * fps)], [0, 1], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });

  const fadeOut = interpolate(frame, [Math.round(4.2 * fps), Math.round(5 * fps)], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  const letters = "KEDARNATH".split("");

  const S = 1280 / 1920;
  return (
    <AbsoluteFill style={{ overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, top: 0, width: 1920, height: 1080, transform: `scale(${S})`, transformOrigin: 'top left', opacity: fadeOut, background: "radial-gradient(ellipse at 50% 40%, #1a0a00 0%, #0a0500 50%, #000 100%)" }}>

      {/* Ripple rings */}
      {[ring1, ring2, ring3].map((r, i) => (
        <div key={i} style={{
          position: "absolute",
          left: "50%", top: "42%",
          width: 200 + i * 60,
          height: 200 + i * 60,
          borderRadius: "50%",
          border: `1px solid rgba(255,215,0,${(1 - r) * 0.3})`,
          transform: `translate(-50%, -50%) scale(${0.5 + r * 1.8})`,
          opacity: 1 - r,
        }} />
      ))}

      {/* Om symbol */}
      <div style={{
        position: "absolute", left: "50%", top: "35%",
        transform: `translate(-50%, -50%) scale(${omScale}) rotate(${omRotate}deg)`,
        filter: `drop-shadow(0 0 ${20 + omGlow * 20}px rgba(255,180,0,${0.4 + omGlow * 0.3}))`,
      }}>
        <svg width="120" height="120" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="58" fill="rgba(255,150,0,0.06)" stroke="rgba(255,215,0,0.3)" strokeWidth="1" />
          <text x="60" y="82" textAnchor="middle" fontSize="72" fill="#FFD700" fontFamily="serif" fontWeight="bold" opacity="0.9">ॐ</text>
        </svg>
      </div>

      {/* Title */}
      <div style={{
        position: "absolute", bottom: "32%", left: 0, right: 0,
        display: "flex", justifyContent: "center", alignItems: "baseline", gap: 2,
        opacity: titleOpacity, transform: `translateY(${titleY}px)`,
      }}>
        {letters.map((l, i) => (
          <span key={i} style={{
            fontSize: 118,
            fontWeight: 900,
            fontFamily: mont,
            color: "#fff",
            letterSpacing: 12,
            lineHeight: 1,
            textShadow: "0 0 60px rgba(255,150,0,0.3), 0 4px 20px rgba(0,0,0,0.8)",
          }}>{l}</span>
        ))}
      </div>

      {/* Divider + subtitle */}
      <div style={{
        position: "absolute", bottom: "22%", left: 0, right: 0,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
        opacity: subOpacity,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <div style={{ width: 80, height: 1, background: "linear-gradient(90deg, transparent, rgba(255,215,0,0.6))" }} />
          <span style={{ color: "rgba(255,215,0,0.5)", fontSize: 10, fontFamily: mont, letterSpacing: 6, textTransform: "uppercase" }}>Jyotirlinga</span>
          <div style={{ width: 80, height: 1, background: "linear-gradient(90deg, rgba(255,215,0,0.6), transparent)" }} />
        </div>
        <span style={{ color: "rgba(255,215,0,0.75)", fontSize: 32, fontFamily: noto, fontWeight: 300, letterSpacing: 6 }}>केदारनाथ</span>
      </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
